import { supabase } from './supabaseClient';
import { User } from '../types/user';
import { PointsService } from './pointsService';
import { setAuthInTransition, clearValidationCache } from '../utils/sessionValidationUtil';

export const AuthService = {
  /**
   * Sign up a new user with terms acceptance
   */
  async signUp(email: string, password: string, username: string, termsAccepted: boolean = true): Promise<{ user: User | null; error: any }> {
    try {
      // Create auth user in Supabase with username in metadata
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            username: username,
            role: 'entrepreneur'
          }
        }
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('User creation failed');

      // The trigger handle_new_user() will automatically create the user profile
      // We need to wait a moment and then fetch the created profile

      // Wait for the trigger to complete (small delay)
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Fetch the user profile created by the trigger
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', authData.user.id)
        .single();

      if (userError) {
        // If profile not found, wait a bit longer and try again
        if (userError.code === 'PGRST116') {
          await new Promise(resolve => setTimeout(resolve, 2000));
          const { data: retryData, error: retryError } = await supabase
            .from('users')
            .select('*')
            .eq('id', authData.user.id)
            .single();

          if (retryError) throw retryError;

          // Award welcome bonus points to the new user
          try {
            await PointsService.awardWelcomeBonus(authData.user.id);
          } catch (pointsError) {
            console.error('Error awarding welcome bonus:', pointsError);
            // Continue even if points award fails
          }

          return { user: retryData as User, error: null };
        }
        throw userError;
      }

      // Update user with terms acceptance if provided
      if (termsAccepted) {
        try {
          await this.updateTermsAcceptance(authData.user.id);
        } catch (termsError) {
          console.error('Error updating terms acceptance:', termsError);
          // Continue even if terms update fails
        }
      }

      // Award welcome bonus points to the new user
      try {
        await PointsService.awardWelcomeBonus(authData.user.id);
      } catch (pointsError) {
        console.error('Error awarding welcome bonus:', pointsError);
        // Continue even if points award fails
      }

      // Return the created user
      return {
        user: userData as User,
        error: null
      };
    } catch (error) {
      console.error('Sign up error:', error);
      return { user: null, error };
    }
  },

  /**
   * Sign in an existing user
   */
  async signIn(email: string, password: string): Promise<{ user: User | null; error: any }> {
    try {
      // Authenticate with Supabase
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Sign in failed');

      // Get user profile from database
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', authData.user.id)
        .single();

      if (userError) throw userError;

      // Update last active timestamp
      await supabase
        .from('users')
        .update({ last_active: new Date() })
        .eq('id', authData.user.id);

      return {
        user: userData as User,
        error: null
      };
    } catch (error) {
      console.error('Sign in error:', error);
      return { user: null, error };
    }
  },

  /**
   * Sign out the current user
   */
  async signOut(): Promise<{ error: any }> {
    try {
      const { error } = await supabase.auth.signOut();

      // If error is due to missing session, consider it successful
      if (error && (error.message?.includes('Auth session missing') || (error as any).name === 'AuthSessionMissingError')) {
        console.warn('Session already expired during sign out');
        return { error: null };
      }

      if (error) throw error;
      return { error: null };
    } catch (error: any) {
      console.error('Sign out error:', error);

      // If it's a session missing error, don't treat it as a failure
      if (error?.message?.includes('Auth session missing') || error?.name === 'AuthSessionMissingError') {
        return { error: null };
      }

      return { error };
    }
  },

  /**
   * Get the current authenticated user
   * @param forceRefresh - If true, always fetch fresh data from database. If false, use cached session data when available.
   */
  async getCurrentUser(forceRefresh: boolean = false): Promise<{ user: User | null; error: any }> {
    try {
      // Get current authenticated session (cached locally, no API call)
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        console.error('Error getting session:', sessionError);
        return { user: null, error: sessionError };
      }

      if (!sessionData.session) {
        return { user: null, error: null }; // Not authenticated
      }

      const authUser = sessionData.session.user;
      console.log('Auth user found in session:', authUser.id);

      // If not forcing refresh, we can construct a user object from session metadata
      // This avoids the database call for most page loads
      if (!forceRefresh && authUser.user_metadata) {
        // Check if we have enough user data in the session metadata
        const sessionUser: User = {
          id: authUser.id,
          email: authUser.email || '',
          username: authUser.user_metadata.username || authUser.user_metadata.name || authUser.email?.split('@')[0] || `user_${authUser.id.substring(0, 8)}`,
          display_name: authUser.user_metadata.display_name || authUser.user_metadata.full_name || authUser.user_metadata.name,
          avatar_url: authUser.user_metadata.avatar_url || authUser.user_metadata.picture,
          role: authUser.user_metadata.role || 'entrepreneur',
          points: authUser.user_metadata.points || 0,
          completed_tasks: authUser.user_metadata.completed_tasks || 0,
          created_at: new Date(authUser.created_at),
          last_active: new Date(),
          // Optional fields
          bio: authUser.user_metadata.bio
        };

        console.log('✅ Using cached session data (no database call)');
        return { user: sessionUser, error: null };
      }

      // Force refresh or no metadata available - fetch from database
      console.log('Fetching user profile from database...');

      // Try to get user profile data - avoid using .single() which can cause 406 errors
      const { data: userProfiles, error: fetchError } = await supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id);

      // If we got data and at least one profile exists
      if (userProfiles && userProfiles.length > 0) {
        const userProfile = userProfiles[0];
        console.log('Existing user profile found:', userProfile.id);

        // Update lastActive timestamp (non-blocking)
        void (async () => {
          try {
            await supabase
              .from('users')
              .update({ last_active: new Date() })
              .eq('id', authUser.id);
            console.log('Updated last_active timestamp');
          } catch (updateError: any) {
            console.warn('Failed to update last_active timestamp:', updateError);
          }
        })();

        return { user: userProfile as User, error: null };
      }

      // No profile found or there was an error, create a new profile
      console.log('Creating new user profile for:', authUser.id);

      // Derive username from auth metadata
      let username = authUser.user_metadata?.name || authUser.user_metadata?.full_name;
      if (!username && authUser.email) {
        username = authUser.email.split('@')[0];
      }
      if (!username) {
        username = `user_${authUser.id.substring(0, 8)}`;
      }

      // Create new user profile
      const newUserProfile = {
        id: authUser.id,
        email: authUser.email,
        username: username,
        role: 'entrepreneur', // Default role for new users
        points: 0,
        completed_tasks: 0,
        created_at: new Date(),
        last_active: new Date()
      };

      // Use upsert with onConflict to handle the case where the user might already exist
      console.log('Attempting to create user profile with id:', authUser.id);
      const { data: insertedProfile, error: insertError } = await supabase
        .from('users')
        .upsert([newUserProfile], {
          onConflict: 'id',  // Specify the column that might cause the conflict
          ignoreDuplicates: false // Update the record if it exists
        })
        .select();

      if (insertError) {
        console.error('Failed to create user profile:', insertError);
        console.error('Error details:', JSON.stringify(insertError));
        // If we got a conflict, try to get the existing user profile
        if (insertError.code === '23505') { // PostgreSQL unique violation code
          console.log('Unique constraint violation, trying to fetch existing user');
          const { data: existingUser, error: fetchError } = await supabase
            .from('users')
            .select('*')
            .eq('id', authUser.id)
            .maybeSingle();

          if (!fetchError && existingUser) {
            console.log('Found existing user profile:', existingUser.id);
            return { user: existingUser as User, error: null };
          }
        }
        return { user: null, error: insertError };
      }

      if (!insertedProfile || insertedProfile.length === 0) {
        return { user: null, error: new Error('Profile created but no data returned') };
      }

      console.log('New user profile created successfully:', insertedProfile[0]);
      return { user: insertedProfile[0] as User, error: null };

    } catch (error) {
      console.error('Get current user error:', error);
      return { user: null, error };
    }
  },

  /**
   * Send password reset email
   */
  async sendPasswordResetEmail(email: string): Promise<{ success: boolean; error?: any }> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Password reset email error:', error);
      return { success: false, error };
    }
  },

  /**
   * Update password using reset token
   */
  async updatePassword(newPassword: string): Promise<{ success: boolean; error?: any }> {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Password update error:', error);
      return { success: false, error };
    }
  },

  /**
   * Sign in with an OAuth provider
   */
  async signInWithProvider(provider: 'google' | 'github' | 'linkedin_oidc'): Promise<{ success: boolean; error?: any }> {
    try {
      // Clear any existing session data first to avoid conflicts
      await supabase.auth.signOut({ scope: 'local' });
      
      // Clear validation cache and mark auth as in transition
      // This prevents validation failures during the OAuth redirect flow
      clearValidationCache();
      setAuthInTransition(true);

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            prompt: 'select_account' // Forces Google to show account selection screen
          }
        }
      });

      // Log the OAuth URL for debugging
      console.log(`Redirecting to ${provider} OAuth:`, data?.url);

      if (error) {
        // Reset transition flag on error
        setAuthInTransition(false);
        throw error;
      }
      if (!data?.url) {
        setAuthInTransition(false);
        throw new Error(`Failed to get ${provider} OAuth URL`);
      }

      // Supabase handles the redirect to the provider and then back to your app
      // Note: setAuthInTransition(true) will persist until the callback page clears it
      return { success: true };
    } catch (error) {
      console.error(`Sign in with ${provider} error:`, error);
      setAuthInTransition(false);
      return { success: false, error };
    }
  },

  /**
   * Update user's terms acceptance status
   */
  async updateTermsAcceptance(userId: string, privacyVersion: string = 'v1.0', termsVersion: string = 'v1.0'): Promise<{ success: boolean; error?: any }> {
    try {
      const { error: updateError } = await supabase
        .from('users')
        .update({
          terms_accepted: true,
          terms_accepted_at: new Date().toISOString(),
          privacy_policy_version: privacyVersion,
          terms_of_service_version: termsVersion
        })
        .eq('id', userId);

      if (updateError) throw updateError;

      // Log the consent for audit trail
      try {
        await supabase.rpc('log_privacy_consent', {
          p_user_id: userId,
          p_consent_type: 'terms_acceptance',
          p_consent_given: true,
          p_privacy_version: privacyVersion,
          p_terms_version: termsVersion,
          p_context: JSON.stringify({ source: 'signup', timestamp: new Date().toISOString() })
        });
      } catch (logError) {
        console.error('Failed to log privacy consent:', logError);
        // Continue even if logging fails
      }

      return { success: true };
    } catch (error) {
      console.error('Update terms acceptance error:', error);
      return { success: false, error };
    }
  },

  /**
   * Check if user has accepted current terms
   */
  async hasAcceptedTerms(userId: string): Promise<{ accepted: boolean; error?: any }> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('terms_accepted, terms_accepted_at, privacy_policy_version, terms_of_service_version')
        .eq('id', userId)
        .single();

      if (error) throw error;

      return {
        accepted: data?.terms_accepted || false,
        error: null
      };
    } catch (error) {
      console.error('Check terms acceptance error:', error);
      return { accepted: false, error };
    }
  }
};
